import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  Tabs,
  Tab,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { poService } from "../../services/po.service";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
} from "../../../../app.interface";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { useAuth } from "../../../auth/hooks/useAuth";
import {
  PurchaseOrder,
  POStatus,
  ListPOsRequest,
  POActivityLog,
} from "../../types/po.types";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { POActivityTimeline } from "../../components/ActivityTimeline/POActivityTimeline";
import { ReceiveItemsDialog } from "./ReceiveItemsDialog";
import { hasPermission } from "../../../auth/utils/permission.utils";

// PO Form Dialog Component
const POFormDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  po?: PurchaseOrder | null;
  onSuccess: () => void;
}> = ({ open, onClose, po, onSuccess }) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [step, setStep] = React.useState<"select" | "form">(
    po ? "form" : "select"
  );
  const [completedRFQs, setCompletedRFQs] = React.useState<any[]>([]);
  const [selectedRFQ, setSelectedRFQ] = React.useState<any | null>(null);
  const [rfqPage, setRfqPage] = React.useState(0);
  const [rfqPageSize, setRfqPageSize] = React.useState(10);
  const [rfqTotal, setRfqTotal] = React.useState(0);

  const [deliveryAddress, setDeliveryAddress] = React.useState("");
  const [deliveryDate, setDeliveryDate] = React.useState(
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );
  const [paymentTerms, setPaymentTerms] = React.useState("");
  const [notes, setNotes] = React.useState("");

  React.useEffect(() => {
    if (open && !po) {
      loadCompletedRFQs();
    }
  }, [open, po]);

  const loadCompletedRFQs = async () => {
    setLoading(true);
    try {
      const response = await poService.getCompletedRFQs({
        pageIndex: rfqPage,
        pageSize: rfqPageSize,
      });
      if (response.success && response.data) {
        setCompletedRFQs(response.data.rfqs || []);
        setRfqTotal(response.data.totalCount || 0);
      }
    } catch (error: any) {
      console.error("Error loading completed RFQs:", error);
      toast.error("Failed to load available RFQs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePO = async () => {
    if (!selectedRFQ || !paymentTerms.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const response = await poService.createPO({
        rfq_id: selectedRFQ._id,
        delivery_terms: deliveryAddress.trim() || undefined,
        expected_delivery_date: deliveryDate.getTime(),
        payment_terms: paymentTerms.trim(),
        notes: notes.trim() || undefined,
      });

      if (response.success) {
        toast.success("Purchase Order created successfully");
        onSuccess();
        onClose();
        // Reset form
        setSelectedRFQ(null);
        setDeliveryAddress("");
        setPaymentTerms("");
        setNotes("");
        setStep("select");
      }
    } catch (error: any) {
      console.error("Error creating PO:", error);
      toast.error("Failed to create Purchase Order");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedRFQ(null);
    setDeliveryAddress("");
    setPaymentTerms("");
    setNotes("");
    setStep(po ? "form" : "select");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon>shopping_cart</Icon>
          {po ? "Edit Purchase Order" : "Create Purchase Order"}
        </Box>
      </DialogTitle>

      <DialogContent>
        {step === "select" && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Select a completed RFQ to create a Purchase Order. Only RFQs with
              accepted quotations are available.
            </Alert>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                  Loading available RFQs...
                </Typography>
              </Box>
            ) : completedRFQs.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}>
                  inventory_2
                </Icon>
                <Typography variant="h6" color="text.secondary">
                  No completed RFQs available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete some RFQs first to create Purchase Orders
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {/* Check if any RFQs have incomplete data */}
                {completedRFQs.some((rfq) => !rfq.supplier_name) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some RFQs have incomplete supplier information. This may
                    indicate a backend data issue.
                  </Alert>
                )}
                {completedRFQs.map((rfq) => (
                  <Paper
                    key={rfq._id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.05
                        ),
                        borderColor: "primary.main",
                      },
                      ...(selectedRFQ?._id === rfq._id && {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderColor: "primary.main",
                      }),
                    }}
                    onClick={() => {
                      setSelectedRFQ(rfq);
                      setStep("form");
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {rfq.rfq_number}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          <Icon
                            sx={{
                              fontSize: 16,
                              mr: 0.5,
                              verticalAlign: "middle",
                            }}
                          >
                            business
                          </Icon>
                          Supplier:{" "}
                          {rfq.supplier_name || "Missing Supplier Data"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Icon
                            sx={{
                              fontSize: 16,
                              mr: 0.5,
                              verticalAlign: "middle",
                            }}
                          >
                            description
                          </Icon>
                          PR: {rfq.purchase_request_number}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="h6"
                          color="success.main"
                          sx={{ mb: 1 }}
                        >
                          ${rfq.total_amount?.toLocaleString() || "0"}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Icon>check</Icon>}
                        >
                          Select
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {step === "form" && selectedRFQ && (
          <Box>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Icon>assignment</Icon>
                  Selected RFQ
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setStep("select")}
                  startIcon={<Icon>swap_horiz</Icon>}
                >
                  Change RFQ
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    RFQ Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedRFQ.rfq_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedRFQ.supplier_name || "Missing Supplier Data"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="success.main"
                  >
                    ${selectedRFQ.total_amount?.toLocaleString() || "0"}{" "}
                    {selectedRFQ.currency || "USD"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Estimated Delivery
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedRFQ.estimated_delivery_days || "N/A"} days
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Typography
              variant="h6"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Icon>local_shipping</Icon>
              Delivery Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Delivery Address"
                  multiline
                  rows={3}
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Enter the complete delivery address"
                  error={!deliveryAddress.trim()}
                  helperText={
                    !deliveryAddress.trim()
                      ? "Delivery address is required"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ mr: 1, mt: 1 }}>location_on</Icon>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Expected Delivery Date"
                  value={deliveryDate.toISOString().split("T")[0]}
                  onChange={(e) => setDeliveryDate(new Date(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <Icon sx={{ mr: 1 }}>event</Icon>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Payment Terms"
                  multiline
                  rows={2}
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder="Enter payment terms (e.g., Net 30, Due on receipt, 2/10 Net 30, etc.)"
                  error={!paymentTerms.trim()}
                  helperText={
                    !paymentTerms.trim() ? "Payment terms are required" : ""
                  }
                  InputProps={{
                    startAdornment: <Icon sx={{ mr: 1, mt: 1 }}>payment</Icon>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes or special instructions for this purchase order"
                  InputProps={{
                    startAdornment: <Icon sx={{ mr: 1, mt: 1 }}>note</Icon>,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        {step === "form" && (
          <LoadingButton
            onClick={handleCreatePO}
            loading={saving}
            disabled={!deliveryAddress.trim() || !paymentTerms.trim()}
            variant="contained"
            startIcon={<Icon>shopping_cart</Icon>}
          >
            Create Purchase Order
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface StatusConfig {
  label: string;
  color: string;
  icon: string;
  bgColor: string;
  canEdit: boolean;
  canDelete: boolean;
  canSubmit?: boolean;
  canApprove?: boolean;
  canReject?: boolean;
  canOrder?: boolean;
  canReceive?: boolean;
}

const statusConfig: Record<POStatus, StatusConfig> = {
  draft: {
    label: "Draft",
    color: "#64748B",
    icon: "edit",
    bgColor: "#F1F5F9",
    canEdit: true,
    canDelete: true,
    canSubmit: true,
  },
  submitted: {
    label: "Submitted",
    color: "#3B82F6",
    icon: "send",
    bgColor: "#EFF6FF",
    canEdit: false,
    canDelete: false,
    canApprove: true,
    canReject: true,
  },
  approved: {
    label: "Approved",
    color: "#059669",
    icon: "check_circle",
    bgColor: "#ECFDF5",
    canEdit: false,
    canDelete: false,
    canOrder: true,
  },
  rejected: {
    label: "Rejected",
    color: "#DC2626",
    icon: "cancel",
    bgColor: "#FEF2F2",
    canEdit: false,
    canDelete: false,
  },
  ordered: {
    label: "Ordered",
    color: "#7C3AED",
    icon: "local_shipping",
    bgColor: "#F3E8FF",
    canEdit: false,
    canDelete: false,
    canReceive: true,
  },
  received: {
    label: "Received",
    color: "#0891B2",
    icon: "inventory",
    bgColor: "#ECFEFF",
    canEdit: false,
    canDelete: false,
  },
  closed: {
    label: "Closed",
    color: "#6B7280",
    icon: "task_alt",
    bgColor: "#F9FAFB",
    canEdit: false,
    canDelete: false,
  },
};

export const POList: React.FC = () => {
  const [pos, setPOs] = React.useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = React.useState(false);
  const [selectedPO, setSelectedPO] = React.useState<PurchaseOrder | null>(
    null
  );
  const [selectedPODetails, setSelectedPODetails] =
    React.useState<PurchaseOrder | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuPOId, setMenuPOId] = React.useState<string | null>(null);

  // Activity logs states
  const [activityLogs, setActivityLogs] = React.useState<POActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [activityTotal, setActivityTotal] = React.useState(0);
  const [activityPage, setActivityPage] = React.useState(1);
  const [activityPageSize] = React.useState(10);

  // Drawer tab state
  const [drawerTab, setDrawerTab] = React.useState(0);

  const { user } = useAuth();

  // Load activity logs for selected PO (from embedded data)
  const loadActivityLogs = (po: PurchaseOrder, page: number = 1) => {
    setActivityLoading(true);
    try {
      const response = poService.getRFQActivityLogs(
        po,
        page - 1, // Convert to 0-based indexing
        activityPageSize
      );

      setActivityLogs(response.activities || []);
      setActivityTotal(response.totalCount || 0);
      setActivityPage(page);
    } catch (error: any) {
      console.error("Error loading activity logs:", error);
      toast.error("Failed to load activity history");
    } finally {
      setActivityLoading(false);
    }
  };

  const handleActivityPageChange = (page: number) => {
    if (selectedPODetails) {
      loadActivityLogs(selectedPODetails, page);
    }
  };

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter POs by their current status",
      options: [
        {
          id: "draft",
          label: "Draft",
          value: "draft",
          icon: "edit",
          color: "#64748B",
          description: "POs in draft state",
        },
        {
          id: "submitted",
          label: "Submitted",
          value: "submitted",
          icon: "send",
          color: "#3B82F6",
          description: "POs submitted for approval",
        },
        {
          id: "approved",
          label: "Approved",
          value: "approved",
          icon: "check_circle",
          color: "#059669",
          description: "POs approved for ordering",
        },
        {
          id: "rejected",
          label: "Rejected",
          value: "rejected",
          icon: "cancel",
          color: "#DC2626",
          description: "POs rejected",
        },
        {
          id: "ordered",
          label: "Ordered",
          value: "ordered",
          icon: "local_shipping",
          color: "#7C3AED",
          description: "POs ordered from supplier",
        },
        {
          id: "received",
          label: "Received",
          value: "received",
          icon: "inventory",
          color: "#0891B2",
          description: "POs received",
        },
        {
          id: "closed",
          label: "Closed",
          value: "closed",
          icon: "task_alt",
          color: "#6B7280",
          description: "POs completed and closed",
        },
      ],
    },
  ];

  const getStatusChip = (status: POStatus) => {
    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        icon={<Icon sx={{ fontSize: "14px !important" }}>{config.icon}</Icon>}
        sx={{
          color: config.color,
          backgroundColor: config.bgColor,
          fontWeight: 500,
          fontSize: "12px",
          height: "24px",
        }}
      />
    );
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(timestamp));
  };

  const columns: TableColumn[] = [
    {
      id: "po_number",
      header: "PO Number",
      field: "po_number",
      type: "action",
      sortable: true,
      accessor: (row: Record<string, any>) => row.po_number,
      renderAction: (row: WithId & Record<string, any>) => {
        const po = row as PurchaseOrder;
        return po.po_number ? (
          <Typography variant="body2" fontWeight={500}>
            {po.po_number}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label="Draft"
              size="small"
              sx={{
                bgcolor: "grey.100",
                color: "text.secondary",
                fontSize: "0.75rem",
                height: 24,
              }}
            />
            <Tooltip
              title="PO number will be generated when submitted for approval"
              arrow
            >
              <Icon sx={{ fontSize: 16, color: "text.disabled" }}>info</Icon>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: "vendor_details",
      header: "Vendor",
      field: "vendor_details.supplier_name",
      type: "text",
      sortable: false,
      accessor: (row: Record<string, any>) => row.vendor_details?.supplier_name,
    },
    {
      id: "total_amount",
      header: "Total Amount",
      field: "total_amount",
      type: "money",
      sortable: true,
      accessor: (row: Record<string, any>) => row.total_amount,
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "status",
      sortable: true,
      accessor: (row: Record<string, any>) => row.status,
      statusConfig: {
        draft: { label: "Draft", color: "gray" },
        submitted: { label: "Submitted", color: "blue" },
        approved: { label: "Approved", color: "green" },
        rejected: { label: "Rejected", color: "rose" },
        ordered: { label: "Ordered", color: "blue" },
        received: { label: "Received", color: "green" },
        closed: { label: "Closed", color: "gray" },
      },
    },
    {
      id: "created_at",
      header: "Created",
      field: "created_at",
      type: "datetime",
      sortable: true,
      accessor: (row: Record<string, any>) => row.created_at,
    },
    {
      id: "actions",
      header: "Actions",
      field: "actions",
      type: "action",
      sortable: false,
      width: "80px",
      renderAction: (row: WithId & Record<string, any>) => {
        const po = row as PurchaseOrder;
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title="More Actions">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setMenuPOId(po._id);
                }}
                sx={{ color: "text.secondary" }}
              >
                <Icon>more_vert</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const loadPOs = async () => {
    setLoading(true);
    try {
      const params: ListPOsRequest = {
        pageIndex: page,
        pageSize,
        sortField,
        sortOrder,
        searchQuery: search || undefined,
        status: filters.status || undefined,
        // Only include date filters if they have valid values
        ...(filters.startDate &&
          typeof filters.startDate === "number" && {
            startDate: filters.startDate,
          }),
        ...(filters.endDate &&
          typeof filters.endDate === "number" && { endDate: filters.endDate }),
        dateField: filters.dateField || "created_at",
      };

      const response = await poService.listPOs(params);
      if (response.success && response.data) {
        setPOs(response.data.purchase_orders || []);
        setTotalItems(response.data.totalCount || 0);
      } else {
        throw new Error(
          response.error?.message || "Failed to load Purchase Orders"
        );
      }
    } catch (error: any) {
      console.error("Error loading Purchase Orders:", error);
      toast.error(error.message || "Failed to load Purchase Orders");
      setPOs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadPOs();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const handleSubmitPO = async (poId: string) => {
    setSaving(true);
    try {
      const response = await poService.submitPO({ _id: poId });
      if (response.success) {
        toast.success("Purchase Order submitted successfully");
        loadPOs();
      } else {
        throw new Error(response.error?.message || "Failed to submit PO");
      }
    } catch (error: any) {
      console.error("Error submitting PO:", error);
      toast.error(error.message || "Failed to submit PO");
    } finally {
      setSaving(false);
      handleMenuClose();
    }
  };

  const handleApprovePO = async (poId: string) => {
    setSaving(true);
    try {
      const response = await poService.approvePO({ _id: poId });
      if (response.success) {
        toast.success("Purchase Order approved successfully");
        loadPOs();
      } else {
        throw new Error(response.error?.message || "Failed to approve PO");
      }
    } catch (error: any) {
      console.error("Error approving PO:", error);
      toast.error(error.message || "Failed to approve PO");
    } finally {
      setSaving(false);
      handleMenuClose();
    }
  };

  const handleRejectPO = async (poId: string, reason: string) => {
    setSaving(true);
    try {
      const response = await poService.rejectPO({
        _id: poId,
        rejection_reason: reason,
      });
      if (response.success) {
        toast.success("Purchase Order rejected successfully");
        loadPOs();
      } else {
        throw new Error(response.error?.message || "Failed to reject PO");
      }
    } catch (error: any) {
      console.error("Error rejecting PO:", error);
      toast.error(error.message || "Failed to reject PO");
    } finally {
      setSaving(false);
      handleMenuClose();
    }
  };

  const handleOrderPO = async (poId: string) => {
    setSaving(true);
    try {
      const response = await poService.orderPO({ _id: poId });
      if (response.success) {
        toast.success("Purchase Order placed successfully");
        loadPOs();
      } else {
        throw new Error(response.error?.message || "Failed to order PO");
      }
    } catch (error: any) {
      console.error("Error ordering PO:", error);
      toast.error(error.message || "Failed to order PO");
    } finally {
      setSaving(false);
      handleMenuClose();
    }
  };

  const handleDeletePO = async () => {
    if (!selectedPO) return;

    setSaving(true);
    try {
      const response = await poService.deletePO({ _id: selectedPO._id });
      if (response.success) {
        toast.success("Purchase Order deleted successfully");
        loadPOs();
        handleCloseDeleteDialog();
      } else {
        throw new Error(response.error?.message || "Failed to delete PO");
      }
    } catch (error: any) {
      console.error("Error deleting PO:", error);
      toast.error(error.message || "Failed to delete PO");
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    console.log("Row clicked:", row);
    const po = pos.find((p) => p._id === row._id);
    console.log("Found PO:", po);
    if (po) {
      setSelectedPODetails(po);
      loadActivityLogs(po);
      console.log("Drawer should open for PO:", po._id);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPOId(null);
  };

  const handleOpenFormDialog = (po?: PurchaseOrder) => {
    console.log("handleOpenFormDialog called with PO:", po);
    setSelectedPO(po || null);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setSelectedPO(null);
    setFormDialogOpen(false);
  };

  const handleSuccess = () => {
    console.log("PO operation successful, reloading POs...");
    loadPOs();
  };

  const handleOpenDeleteDialog = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedPO(null);
    setDeleteDialogOpen(false);
  };

  const handleOpenReceiveDialog = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setReceiveDialogOpen(true);
  };

  const handleCloseReceiveDialog = () => {
    setSelectedPO(null);
    setReceiveDialogOpen(false);
  };

  const handleDrawerClose = () => {
    setSelectedPODetails(null);
    setActivityLogs([]);
    setDrawerTab(0);
  };

  const selectedMenuPO = pos.find((po) => po._id === menuPOId);

  return (
    <Fragment>
      <PageHeader
        title="Purchase Orders"
        subtitle="Manage purchase orders from approved RFQs • Click any row to view details"
        icon="shopping_cart"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleOpenFormDialog()}
            disabled={
              !hasPermission(
                "inventory.purchase_orders.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
          >
            Create PO
          </Button>
        }
      />

      <Box sx={{ p: 3 }}>
        {/* Search and Filters */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search purchase orders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ""}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="ordered">Ordered</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<Icon>refresh</Icon>}
                onClick={loadPOs}
                disabled={loading}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Box>

        {loading || pos.length > 0 ? (
          <AppTable
            columns={columns}
            data={pos}
            loading={loading}
            pagination={{
              total: totalItems,
              page,
              pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
            onSort={(field: string, direction: "asc" | "desc") => {
              setSortField(field);
              setSortOrder(direction);
            }}
            onRowClick={handleRowClick}
            toolbarActions={{
              export: {
                label: "Export",
                is_disabled: pos.length === 0,
              },
            }}
          />
        ) : (
          <EmptyState
            title="No Purchase Orders"
            description="Create your first purchase order from a completed RFQ"
            icon="shopping_cart"
          />
        )}

        {/* Dialogs */}
        <POFormDialog
          open={formDialogOpen}
          onClose={handleCloseFormDialog}
          po={selectedPO}
          onSuccess={handleSuccess}
        />

        <ReceiveItemsDialog
          open={receiveDialogOpen}
          onClose={handleCloseReceiveDialog}
          po={selectedPO}
          onSuccess={handleSuccess}
        />

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeletePO}
          title="Purchase Order"
          itemName={selectedPO?.po_number || "this purchase order"}
          itemDescription="This action cannot be undone and all associated data will be permanently deleted."
          loading={saving}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { minWidth: 180 },
          }}
        >
          {selectedMenuPO && (
            <>
              {selectedMenuPO.status === "draft" && (
                <>
                  <MenuItem
                    onClick={() => {
                      handleOpenFormDialog(selectedMenuPO);
                      handleMenuClose();
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_orders.edit",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 2 }}>edit</Icon>
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleSubmitPO(selectedMenuPO._id);
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_orders.submit",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{ color: "primary.main" }}
                  >
                    <Icon sx={{ mr: 2 }}>send</Icon>
                    Submit for Approval
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleOpenDeleteDialog(selectedMenuPO);
                      handleMenuClose();
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_orders.delete",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{ color: "error.main" }}
                  >
                    <Icon sx={{ mr: 2 }}>delete</Icon>
                    Delete
                  </MenuItem>
                </>
              )}

              {selectedMenuPO.status === "submitted" && (
                <>
                  <MenuItem
                    onClick={() => {
                      handleApprovePO(selectedMenuPO._id);
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_orders.approve",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{ color: "success.main" }}
                  >
                    <Icon sx={{ mr: 2 }}>check_circle</Icon>
                    Approve
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleRejectPO(selectedMenuPO._id, "Rejected by admin");
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.purchase_orders.reject",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{ color: "error.main" }}
                  >
                    <Icon sx={{ mr: 2 }}>cancel</Icon>
                    Reject
                  </MenuItem>
                </>
              )}

              {selectedMenuPO.status === "approved" && (
                <MenuItem
                  onClick={() => {
                    handleOrderPO(selectedMenuPO._id);
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.purchase_orders.order",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{ color: "primary.main" }}
                >
                  <Icon sx={{ mr: 2 }}>shopping_cart</Icon>
                  Place Order
                </MenuItem>
              )}

              {selectedMenuPO.status === "ordered" && (
                <MenuItem
                  onClick={() => {
                    handleOpenReceiveDialog(selectedMenuPO);
                    handleMenuClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.purchase_orders.receive",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{ color: "success.main" }}
                >
                  <Icon sx={{ mr: 2 }}>inventory</Icon>
                  Receive Items
                </MenuItem>
              )}
            </>
          )}
        </Menu>

        {/* PO Details Drawer */}
        <Drawer
          anchor="right"
          open={Boolean(selectedPODetails)}
          onClose={handleDrawerClose}
          PaperProps={{
            sx: { width: { xs: "100%", sm: 600 } },
          }}
        >
          {selectedPODetails && (
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h5" fontWeight={600}>
                  Purchase Order Details
                </Typography>
                <IconButton onClick={handleDrawerClose}>
                  <Icon>close</Icon>
                </IconButton>
              </Box>

              <Tabs
                value={drawerTab}
                onChange={(_, newValue) => setDrawerTab(newValue)}
                sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Overview" />
                <Tab label="Items" />
                <Tab label="Activity" />
              </Tabs>

              {/* Tab Panel 0: Overview */}
              {drawerTab === 0 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          PO Number
                        </Typography>
                        {selectedPODetails.po_number ? (
                          <Typography variant="h6" fontWeight={600}>
                            {selectedPODetails.po_number}
                          </Typography>
                        ) : (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Chip
                                label="Not Generated Yet"
                                size="small"
                                sx={{
                                  bgcolor: "warning.light",
                                  color: "warning.dark",
                                  fontSize: "0.75rem",
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              PO number will be auto-generated when submitted
                              for approval
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Status
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {getStatusChip(selectedPODetails.status)}
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Supplier
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {selectedPODetails.vendor_details?.supplier_name}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Total Amount
                        </Typography>
                        <Typography
                          variant="h6"
                          color="success.main"
                          fontWeight={600}
                        >
                          {formatCurrency(
                            selectedPODetails.total_amount,
                            selectedPODetails.currency
                          )}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RFQ Reference
                        </Typography>
                        <Typography variant="body2">
                          <strong>RFQ Number:</strong>{" "}
                          {selectedPODetails.rfq_reference?.rfq_number}
                        </Typography>
                        <Typography variant="body2">
                          <strong>PR Number:</strong>{" "}
                          {
                            selectedPODetails.rfq_reference
                              ?.purchase_request_number
                          }
                        </Typography>
                      </Paper>
                    </Grid>
                    {selectedPODetails.delivery_terms && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            gutterBottom
                          >
                            Delivery Terms
                          </Typography>
                          <Typography variant="body2">
                            {selectedPODetails.delivery_terms}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedPODetails.payment_terms && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            gutterBottom
                          >
                            Payment Terms
                          </Typography>
                          <Typography variant="body2">
                            {selectedPODetails.payment_terms}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedPODetails.notes && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            gutterBottom
                          >
                            Notes
                          </Typography>
                          <Typography variant="body2">
                            {selectedPODetails.notes}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Tab Panel 1: Items */}
              {drawerTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Purchase Order Items
                  </Typography>
                  {selectedPODetails.items &&
                  selectedPODetails.items.length > 0 ? (
                    <Box>
                      {selectedPODetails.items.map((item, index) => (
                        <Paper
                          key={index}
                          sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Item Name
                              </Typography>
                              <Typography variant="body1" fontWeight={500}>
                                {item.item_name}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Quantity
                              </Typography>
                              <Typography variant="body1">
                                {item.quantity} {item.uom}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Unit Price
                              </Typography>
                              <Typography variant="body1">
                                {formatCurrency(item.unit_price)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Total Price
                              </Typography>
                              <Typography
                                variant="h6"
                                color="success.main"
                                fontWeight={600}
                              >
                                {formatCurrency(item.total_price)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info">
                      No items found for this purchase order.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Tab Panel 2: Activity */}
              {drawerTab === 2 && (
                <Box sx={{ p: 3 }}>
                  <POActivityTimeline
                    activities={activityLogs}
                    loading={activityLoading}
                    total={activityTotal}
                    page={activityPage}
                    pageSize={activityPageSize}
                    onPageChange={handleActivityPageChange}
                    showPagination={true}
                  />
                </Box>
              )}
            </Box>
          )}
        </Drawer>
      </Box>
    </Fragment>
  );
};
